/**
 * Login Form Component
 * 
 * This component provides a login form with email/password authentication
 * and integration with the TOTP verification system.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Typography,
  CircularProgress,
  useTheme,
  alpha,
  Fade
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
  Person as PersonIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { useAuth } from './AuthProvider';

interface LoginFormProps {
  /** Prefill sign-in identifier (e.g. after accepting an invitation). */
  initialEmail?: string;
  onSuccess: () => void;
  onLoginAttempt?: (email: string, password: string) => void;
  onSwitchToRegister?: () => void;
  onForgotPassword?: (email: string) => void;
  className?: string;
}

interface FormData {
  email: string;
  password: string;
}

interface ValidationErrors {
  email?: string;
  password?: string;
  general?: string;
}

const LoginForm: React.FC<LoginFormProps> = ({
  initialEmail = '',
  onSuccess,
  onLoginAttempt,
  onSwitchToRegister,
  onForgotPassword,
  className = '',
}) => {
  const { login, isLoading, error, clearError } = useAuth();

  const [formData, setFormData] = useState<FormData>({
    email: initialEmail.trim(),
    password: '',
  });
  
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [showPassword, setShowPassword] = useState<boolean>(false);

  // Helper function to check if error is password-related
  const isPasswordError = (errorMsg: string | null): boolean => {
    if (!errorMsg) return false;
    const lowerError = errorMsg.toLowerCase();
    return (
      lowerError.includes('incorrect password') ||
      lowerError.includes('invalid password') ||
      lowerError.includes('wrong password') ||
      lowerError.includes('password is incorrect')
    );
  };

  // Helper function to get password-specific error message
  const getPasswordErrorMessage = (errorMsg: string | null): string | undefined => {
    if (!errorMsg) return undefined;
    if (isPasswordError(errorMsg)) {
      return 'The password you entered is incorrect. Please try again.';
    }
    return undefined;
  };

  useEffect(() => {
    const next = initialEmail?.trim();
    if (next) {
      setFormData((prev) => (prev.email === next ? prev : { ...prev, email: next }));
    }
  }, [initialEmail]);

  // Clear errors when form data changes (but preserve password errors if password field changes)
  useEffect(() => {
    // Only clear general errors, not field-specific validation errors
    // Password errors will be cleared when password field changes
  }, [formData]);

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error for this field
    if (validationErrors[name as keyof ValidationErrors]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
    
    // Clear general error if password field changes and there's a password error
    if (name === 'password' && error && isPasswordError(error)) {
      clearError();
    }
    
    // Clear general error if email field changes and there's a "user not found" error
    if (name === 'email' && error && error.includes('User not found')) {
      clearError();
    }
  };

  // Validate form data
  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    
    setValidationErrors(errors);
    const isValid = Object.keys(errors).length === 0;
    return isValid;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      // Notify parent about login attempt (for TOTP resend functionality)
      if (onLoginAttempt) {
        onLoginAttempt(formData.email, formData.password);
      }
      
      const result = await login(formData.email, formData.password);
      if (result && result.success) {
        onSuccess();
      }
    } catch (err) {
      // Error is handled by the auth context
    }
  };

  const theme = useTheme();

  return (
    <Box className={className}>
      <Fade in timeout={600}>
        <Box>
          {/* Login Form */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            {/* Email Field */}
            <TextField
              fullWidth
              type="email"
              label="Email Address"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter your email address"
              disabled={isLoading}
              autoComplete="username"
              autoFocus
              error={!!validationErrors.email}
              helperText={validationErrors.email}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                },
              }}
            />

            {/* Password Field */}
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label="Password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Enter your password"
              disabled={isLoading}
              autoComplete="current-password"
              error={!!validationErrors.password || isPasswordError(error)}
              helperText={validationErrors.password || getPasswordErrorMessage(error)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={isLoading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                },
              }}
            />

            {/* Forgot Password link - shows when password is incorrect */}
            {isPasswordError(error) && onForgotPassword && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -1 }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => onForgotPassword(formData.email)}
                  sx={{
                    textTransform: 'none',
                    color: 'primary.main',
                    p: 0,
                    minWidth: 'auto',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Forgot Password?
                </Button>
              </Box>
            )}

            {/* Inline error message (only show if not password error, as password field shows it) */}
            {error && !isPasswordError(error) && (
              <Typography variant="body2" color="error" sx={{ mt: -1 }}>
                {error}
              </Typography>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
              sx={{
                py: 1.5,
                borderRadius: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                textTransform: 'none',
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                boxShadow: `0 4px 14px 0 ${alpha(theme.palette.primary.main, 0.3)}`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                  boxShadow: `0 6px 20px 0 ${alpha(theme.palette.primary.main, 0.4)}`,
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
            </Button>
          </Box>

          {/* Demo button and divider removed */}
        </Box>
      </Fade>
    </Box>
  );
};

export default LoginForm;
